import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Eye } from 'lucide-react';
const images = [{
  src: 'https://cdn.magicpatterns.com/uploads/bhLShit7M5dBH7vbQecp3Q/freepik__fotografa-ultra-realista-nativa-no-cgi-resolucin-8__60896.png',
  caption: 'Rostro completo — Consistencia facial total'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/gBHFA3rr7cL2kPtaxYwbBS/freepik__fotografa-ultra-realista-nativa-no-cgi-resolucin-8__60895.png',
  caption: 'Misma identidad — Diferente ángulo'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/5iQ9VxnDHQgQNiE7zheJd6/freepik__prompt-labiosfotografa-ultra-realista-nativa-no-cg__60893.png',
  caption: 'Detalle de labios — Textura hiperrealista'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/uYwbXdrnh7jhKvbYgifT3g/freepik__prompt-ojosfotografa-ultra-realista-nativa-no-cgi-__60892.png',
  caption: 'Detalle de ojo — Poros y pestañas reales'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/brZuF2pdv1H5pxfgGzmV5o/freepik__fotografa-ultra-realista-nativa-no-cgi-resolucin-8__60890.png',
  caption: 'Pose alternativa — Identidad intacta'
}];
const swipeConfidenceThreshold = 10000;
const swipePower = (offset: number, velocity: number) => {
  return Math.abs(offset) * velocity;
};
export function ResultsSection() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);
  const paginate = (newDirection: number) => {
    setDirection(newDirection);
    setCurrent(prev => {
      let next = prev + newDirection;
      if (next < 0) next = images.length - 1;
      if (next >= images.length) next = 0;
      return next;
    });
  };
  const goTo = (index: number) => {
    setDirection(index > current ? 1 : -1);
    setCurrent(index);
  };
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 300 : -300,
      opacity: 0
    })
  };
  return <section className="py-24 px-5 relative overflow-hidden bg-gradient-to-b from-[#0a0a0f] via-[#0d0d14] to-[#0a0a0f]" data-id="element-176">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#00d4ff]/5 rounded-full blur-[150px] pointer-events-none" data-id="element-177" />

      <div className="max-w-4xl mx-auto relative z-10" data-id="element-178">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12" data-id="element-179">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00d4ff]/10 border border-[#00d4ff]/20 mb-6" data-id="element-180">
            <Eye className="w-4 h-4 text-[#00d4ff]" data-id="element-181" />
            <span className="text-sm font-medium text-[#00d4ff]" data-id="element-182">
              Prueba Visual
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-183">
            Estos son los <span className="text-gradient-cyan" data-id="element-184">Resultados</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto" data-id="element-185">
            Generados con el Método Operativo. Mismo clon, diferentes
            escenarios. Zoom al detalle para que veas la textura de piel real.
          </p>
        </motion.div>

        {/* Slider */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} className="relative" data-id="element-186">
          {/* Image Container */}
          <div className="relative aspect-[3/4] md:aspect-[4/5] max-w-lg mx-auto rounded-2xl overflow-hidden glass-card" data-id="element-187">
            <AnimatePresence initial={false} custom={direction} data-id="element-188">
              <motion.img key={current} src={images[current].src} alt={images[current].caption} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              x: {
                type: 'spring',
                stiffness: 300,
                damping: 30
              },
              opacity: {
                duration: 0.2
              }
            }} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} dragElastic={1} onDragEnd={(_e, {
              offset,
              velocity
            }) => {
              const swipe = swipePower(offset.x, velocity.x);
              if (swipe < -swipeConfidenceThreshold) {
                paginate(1);
              } else if (swipe > swipeConfidenceThreshold) {
                paginate(-1);
              }
            }} className="absolute inset-0 w-full h-full object-cover cursor-grab active:cursor-grabbing" data-id="element-189" />
            </AnimatePresence>

            {/* Gradient overlay at bottom for caption */}
            <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/80 to-transparent z-10 pointer-events-none" data-id="element-190" />

            {/* Caption */}
            <div className="absolute bottom-4 left-0 right-0 z-20 text-center px-4" data-id="element-191">
              <p className="text-white text-sm md:text-base font-medium" data-id="element-192">
                {images[current].caption}
              </p>
            </div>

            {/* Counter */}
            <div className="absolute top-4 right-4 z-20 bg-black/50 backdrop-blur-sm px-3 py-1 rounded-full border border-white/10" data-id="element-193">
              <span className="text-white text-xs font-bold" data-id="element-194">
                {current + 1} / {images.length}
              </span>
            </div>

            {/* Navigation Arrows */}
            <button onClick={() => paginate(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white hover:bg-black/60 transition-colors" aria-label="Anterior" data-id="element-195">
              <ChevronLeft className="w-5 h-5" data-id="element-196" />
            </button>
            <button onClick={() => paginate(1)} className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white hover:bg-black/60 transition-colors" aria-label="Siguiente" data-id="element-197">
              <ChevronRight className="w-5 h-5" data-id="element-198" />
            </button>
          </div>

          {/* Dot Indicators */}
          <div className="flex items-center justify-center gap-2 mt-6" data-id="element-199">
            {images.map((_, index) => <button key={index} onClick={() => goTo(index)} className={`h-2 rounded-full transition-all duration-300 ${index === current ? 'w-8 bg-[#00d4ff]' : 'w-2 bg-white/20 hover:bg-white/40'}`} aria-label={`Ir a imagen ${index + 1}`} data-id="element-200" />)}
          </div>
        </motion.div>
      </div>
    </section>;
}